import React, { useState, useRef, forwardRef, useEffect, useMemo } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import SVG from 'react-inlinesvg';
// Context
import useData from '../context/DataContext';
import { useSlideContent } from '../context/SlideContent';
// Hooks
import useResponsive from '../hooks/useResponsive';
// Helpers
import { shortenParagraph, convertVideoTime } from '../helpers';
import hasValidParameter from './../helpers/hasValidParameter';
// Components
import Accordion from './Accordion';
import LinearProgressBar from './LinearProgressBar';
// Icons
import IconChevronRight from '../../../../assets/images/reskin-images/icon--chevron-right.svg';
import IconLock from '../../../../assets/images/reskin-images/icon--lock-sm.svg';
import CheckIcon from '../../../../assets/images/reskin-images/icon--check-circle.svg';
import CheckIconPurple from '../../../../assets/images/reskin-images/icon--check-circle-purple.svg';
import PlayIcon from '../../../../assets/images/reskin-images/icon--play-sm.svg';
// Images
import ALDImg from '../../../../assets/images/reskin-images/img--ald-direct.png';
import StudyGroupImg from '../../../../assets/images/reskin-images/img--study-groups.png';
import CohortsImg from '../../../../assets/images/reskin-images/img--upcoming-cohorts.jpeg';
import BespokeImg from '../../../../assets/images/reskin-images/img--bespoke-solutions.jpeg';

const getVerticalScrollParent = (element) => {
  let parent = element?.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (
      /(auto|scroll)/.test(overflowY) &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.documentElement;
};

// Desktop nav + video share one vertical scroller. Scrolling a late-module
// behavior into the middle of the viewport pushes the player above the fold
// and looks like a blank content pane.
//
// Target #program-content-wrapper (real content height), not #program-content:
// the latter stretches with the tall flex row, so "nearest" can no-op while
// only its empty lower region is on screen.
const ensureProgramContentVisible = () => {
  const target =
    document.getElementById('program-content-wrapper') ||
    document.getElementById('program-content');
  target?.scrollIntoView({
    behavior: 'instant',
    block: 'nearest',
    inline: 'nearest',
  });
};

const scrollModuleCardToCenter = (moduleCardRef) => {
  const cardEl = moduleCardRef?.current;
  if (!cardEl) return;

  const container = getVerticalScrollParent(cardEl);
  const containerRect = container.getBoundingClientRect();
  const moduleCardRect = cardEl.getBoundingClientRect();
  const isDocumentScroller =
    container === document.documentElement || container === document.body;

  const scrollOffset =
    moduleCardRect.top -
    (isDocumentScroller ? 0 : containerRect.top) +
    container.scrollTop -
    (container.clientHeight - moduleCardRect.height) * 0.2;

  const maxScrollTop = Math.max(
    0,
    container.scrollHeight - container.clientHeight
  );
  const scrollTop = Math.min(maxScrollTop, Math.max(0, scrollOffset));

  if (isDocumentScroller) {
    window.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  } else {
    container.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  }
};

const ModuleCard = forwardRef(({ moduleData, imgSrc, moduleIndex }, ref) => {
  const {
    title,
    description,
    id: moduleId,
    enrolled_in: moduleEnrolledIn,
    poster,
  } = moduleData;

  const { isLg } = useResponsive();

  const hasPoster = poster?.url && !poster.url.includes('blank');

  const [openBehaviors, setOpenBehaviors] = useState(false);
  const { moduleId: moduleIdParam, behaviorId: behaviorIdParam } = useParams();
  const { setSlideContent } = useSlideContent();
  const pendingBehaviorScrollRef = useRef(
    Number(moduleIdParam) === moduleData?.id && Boolean(behaviorIdParam)
  );

  const isFirstTwo = moduleIndex < 2;
  const isFoundation = title === 'Foundations';
  const isIntroduction = title === 'Making The Most of Admired Leadership';

  const validSections = [
    'notes',
    'examples',
    'questions',
    'exercises',
    'behavior_maps',
    'gift',
  ];
  const isBehaviorTab = hasValidParameter('tab', validSections);

  useEffect(() => {
    const url = new URL(window.location.href);
    const queryParams = new URLSearchParams(url.search);
    const viewParam = queryParams.get('view');

    if (viewParam === 'all') {
      setOpenBehaviors(false);
      pendingBehaviorScrollRef.current = false;
      window.scrollTo({ top: 0 });
      return;
    }

    if (Number(moduleIdParam) === moduleData?.id) {
      setOpenBehaviors(true);
    }

    if (isBehaviorTab) {
      setSlideContent(true);
    }
  }, []);

  useEffect(() => {
    if (!openBehaviors || !pendingBehaviorScrollRef.current) return;

    // User navigated to another module before the deep-link scroll ran.
    if (Number(moduleIdParam) !== moduleData?.id) {
      pendingBehaviorScrollRef.current = false;
      return;
    }

    // Wait for accordion content to mount. Clear pending only after the
    // attempt so React Strict Mode remounts still scroll once.
    const timeoutId = window.setTimeout(() => {
      pendingBehaviorScrollRef.current = false;
      if (isLg) {
        ensureProgramContentVisible();
        return;
      }

      document.getElementById(`behavior-${behaviorIdParam}`)?.scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
        inline: 'nearest',
      });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [openBehaviors, behaviorIdParam, isLg, moduleIdParam, moduleData?.id]);

  const behaviors = useMemo(
    () =>
      moduleData.behaviors.filter(
        ({ is_additional_behavior }) => !is_additional_behavior
      ),
    [moduleData.behaviors]
  );
  const additional_behaviors = useMemo(
    () =>
      moduleData.behaviors.filter(
        ({ is_additional_behavior }) => is_additional_behavior
      ),
    [moduleData.behaviors]
  );

  function getBehaviorViewObject() {
    let word = 'Behaviors'; // Default word
    switch (true) {
      case isFoundation:
        word = 'Foundational Principal';
        break;
      case isIntroduction:
        word = 'Introduction';
        break;
      default:
        break;
    }

    return {
      view: `View ${word}`,
      hide: `Hide ${word}`,
    };
  }

  const viewHideWording = getBehaviorViewObject();

  const handleBehaviorNavClick = () => {
    if (isLg) {
      ensureProgramContentVisible();
    } else {
      setSlideContent(true);
    }
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '320px',
        boxShadow: '0px 10px 50px rgba(0, 0, 0, 0.2)',
      }}
      ref={ref}
      className={`w-full rounded-xl relative overflow-hidden border-t border-gray lg:border-none text-sm ${isFirstTwo && 'bg-purple-100'}`}
    >
      <button
        className="w-full"
        type="button"
        onClick={() => {
          const willOpen = !openBehaviors;
          setOpenBehaviors(willOpen);
          // Abandon deep-link scroll if the user collapses before the timeout;
          // otherwise a cancelled timer leaves pending true and re-fires later.
          if (!willOpen) {
            pendingBehaviorScrollRef.current = false;
            return;
          }
          // On desktop the nav shares a scroller with the player; centering a
          // late module card scrolls the video off-screen (same blank-pane bug).
          if (!isLg) {
            scrollModuleCardToCenter(ref);
          }
        }}
      >
        <div
          style={{
            height: '120px',
          }}
          className="overflow-hidden w-full"
        >
          <img
            src={hasPoster ? poster?.url : imgSrc}
            alt="Module thumbnail image"
            className="object-cover w-full"
            style={{
              height: '120px',
            }}
          />
        </div>

        <div className="h-full flex flex-col items-start p-3 gap-3 flex-auto min-w-0">
          <span
            className="text-charcoal font-extrabold text-lg block text-left"
            style={{ wordBreak: 'break-word' }}
          >
            {title}
          </span>
          {description && (
            <span
              className="text-charcoal text-sm block text-left"
              style={{ wordBreak: 'break-word' }}
            >
              {shortenParagraph(description, 150)}
            </span>
          )}
          <LinearProgressBar module={moduleData} className="w-full " />
          <span className="uppercase font-bold text-link-purple text-left">
            {openBehaviors ? viewHideWording.hide : viewHideWording.view}
          </span>
        </div>
      </button>

      <Accordion isOpen={openBehaviors}>
        {behaviors.map((behaviorData) => {
          const {
            completed,
            title,
            id: behaviorId,
            video_length: videoLength,
            enrolled_in: behaviorEnrolledIn,
          } = behaviorData;

          const isAccessible = moduleEnrolledIn || behaviorEnrolledIn;

          const isCurrentBehavior =
            behaviorIdParam === behaviorId.toString() &&
            moduleIdParam === moduleId.toString();

          return isAccessible ? (
            <NavLink
              to={`/v2/program/${moduleId}/${behaviorId}`}
              onClick={handleBehaviorNavClick}
              type="button"
              key={behaviorId}
              style={{ gap: '20px' }}
              className={`border-t border-gray p-4 lg:hover:bg-purple-200 w-full flex min-w-0 ${
                isCurrentBehavior && 'bg-purple-200'
              }`}
            >
              <div style={{ minWidth: '24px', minHeight: '24px' }}>
                {completed ? (
                  <SVG src={CheckIcon} />
                ) : (
                  <SVG
                    src={PlayIcon}
                    className={isCurrentBehavior ? 'purple-icon' : undefined}
                  />
                )}
              </div>
              <div
                id={`behavior-${behaviorId}`}
                className="flex flex-col min-w-0"
              >
                <span
                  className={`${
                    isCurrentBehavior ? 'text-link-purple' : 'text-charcoal'
                  } font-bold mb-1`}
                  style={{ wordBreak: 'break-word' }}
                >
                  {title}
                </span>
                <span className="text-charcoal text-sm ">
                  {convertVideoTime(videoLength)} |{' '}
                  {behaviorData?.behaviorNumber}
                </span>
              </div>
            </NavLink>
          ) : (
            <button
              type="button"
              key={behaviorId}
              style={{ gap: '20px' }}
              className="border-t border-gray p-4 lg:hover:bg-purple-200 w-full flex justify-start min-w-0"
              disabled
            >
              <div style={{ minWidth: '24px', minHeight: '24px' }}>
                <SVG src={IconLock} />
              </div>
              <div
                id={`behavior-${behaviorId}`}
                className="flex flex-col min-w-0"
              >
                <span
                  className="text-charcoal font-bold mb-1 text-left"
                  style={{ wordBreak: 'break-word' }}
                >
                  {title}
                </span>
                <span className="text-charcoal text-sm text-left">
                  {convertVideoTime(videoLength)} |{' '}
                  {behaviorData?.behaviorNumber}
                </span>
              </div>
            </button>
          );
        })}
        {additional_behaviors.length > 0 && (
          <>
            <div className="bg-middle-gray px-4 py-2 text-white uppercase font-extrabold text-xxs">
              Additional Behaviors of Note
            </div>
            {additional_behaviors.map((behaviorData) => {
              const {
                completed,
                title,
                id: behaviorId,
                video_length: videoLength,
                enrolled_in: behaviorEnrolledIn,
              } = behaviorData;

              const isAccessible = moduleEnrolledIn || behaviorEnrolledIn;

              const isCurrentBehavior =
                behaviorIdParam === behaviorId.toString() &&
                moduleIdParam === moduleId.toString();

              return isAccessible ? (
                <NavLink
                  to={`/v2/program/${moduleId}/${behaviorId}`}
                  onClick={handleBehaviorNavClick}
                  type="button"
                  key={behaviorId}
                  style={{ gap: '20px' }}
                  className={`border-t border-gray p-4 bg-light-chartreuse lg:hover:bg-purple-200 w-full flex min-w-0 ${
                    isCurrentBehavior && 'bg-purple-200'
                  }`}
                >
                  <div style={{ minWidth: '24px', minHeight: '24px' }}>
                    {completed ? (
                      <SVG src={CheckIconPurple} />
                    ) : (
                      <SVG src={PlayIcon} />
                    )}
                  </div>
                  <div
                    id={`behavior-${behaviorId}`}
                    className="flex flex-col min-w-0"
                  >
                    <span
                      className="text-charcoal font-bold mb-1"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {title}
                    </span>
                    <span className="text-charcoal text-sm">
                      {convertVideoTime(videoLength)} |{' '}
                      {behaviorData?.behaviorNumber}
                    </span>
                  </div>
                </NavLink>
              ) : (
                <button
                  type="button"
                  key={behaviorId}
                  style={{ gap: '20px' }}
                  className="border-t border-gray p-4 bg-light-chartreuse lg:hover:bg-purple-200 w-full flex justify-start min-w-0"
                  disabled
                >
                  <div style={{ minWidth: '24px', minHeight: '24px' }}>
                    <SVG src={IconLock} />
                  </div>
                  <div
                    id={`behavior-${behaviorId}`}
                    className="flex flex-col min-w-0"
                  >
                    <span
                      className="text-charcoal font-bold mb-1 text-left"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {title}
                    </span>
                    <span className="text-charcoal text-sm text-left">
                      {convertVideoTime(videoLength)} |{' '}
                      {behaviorData?.behaviorNumber}
                    </span>
                  </div>
                </button>
              );
            })}
          </>
        )}
      </Accordion>
    </div>
  );
});

const ModuleCardRoot = ({ moduleData, imgSrc, index }) => {
  const moduleCardRef = useRef(null);
  return (
    <ModuleCard
      ref={moduleCardRef}
      key={moduleData.id}
      moduleData={moduleData}
      imgSrc={imgSrc}
      moduleIndex={index}
    />
  );
};

export default function ProgramNavigationV2({ navigationSize, isLg }) {
  const [isOpen, setIsOpen] = useState(true);
  useEffect(() => {
    setIsOpen(isLg);
  }, [isLg]);

  const { contentData } = useData();
  const images = [ALDImg, StudyGroupImg, CohortsImg, BespokeImg];

  return (
    <div
      className="flex flex-col p-4 gap-2 bg-faint-charcoal w-full min-w-0 overflow-x-hidden lg:w-auto"
      id="program-nav"
    >
      {navigationSize !== 0 && (
        <div className="flex flex-col gap-4 w-full min-w-0">
          <div
            className="flex items-center justify-between"
            onClick={() => {
              if (isLg) return;
              setIsOpen((currentValue) => !currentValue);
            }}
          >
            <span className="lg:font-sans lg:uppercase text-2xl ml-4 lg:m-0 lg:text-sm font-black">
              Modules
            </span>
            <SVG
              src={IconChevronRight}
              className={`w-5 h-5 mr-2 transform transition-transform duration-200 lg:hidden ${
                isOpen ? 'rotate-90' : ''
              }`}
            />
          </div>
          {isOpen && (
            <div className="flex flex-col items-center gap-4 w-full min-w-0">
              {contentData?.modules.map((moduleData, index) => {
                const imgSrc = images[index % images.length];
                return (
                  <ModuleCardRoot
                    key={moduleData.id}
                    moduleData={moduleData}
                    imgSrc={imgSrc}
                    moduleIndex={index}
                    index={index}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
